import { useEffect, useMemo, useState } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  Background,
  Controls,
  Handle,
  Position,
  ReactFlow,
  type Edge,
  type Node,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

const SUBJECTS = gql`
  query GraphSubjects {
    services {
      name
      classification
      productLine
    }
    clusters {
      name
      region
    }
  }
`;

const SERVICE_GRAPH = gql`
  query ServiceGraph($service: String, $cluster: String) {
    serviceGraph(service: $service, cluster: $cluster) {
      nodes {
        id
        kind
        label
        sub
      }
      edges {
        id
        source
        target
        verb
        confidence
      }
    }
  }
`;

type Kind = 'service' | 'workload' | 'cluster';
interface GNode { id: string; kind: Kind; label: string; sub?: string }
interface GEdge { id: string; source: string; target: string; verb: string; confidence: string }
interface GraphData { serviceGraph: { nodes: GNode[]; edges: GEdge[] } }
interface SubjectsData {
  services: { name: string; classification?: string; productLine?: string }[];
  clusters: { name: string; region?: string }[];
}
interface Focus { kind: 'service' | 'cluster'; name: string }

// ── layout constants ─────────────────────────────────────────────────────────
const COL_SVC_X = 0;
const COL_CL_X = 360;
const SVC_W = 210;
const CL_W = 300;
const WL_W = 268;
const WL_H = 48;
const WL_GAP = 8;
const HEAD_H = 50; // reserves room for a 2-line cluster name so workloads never overlap it
const PAD = 12;

/** Cluster container — a labelled region; the workloads inside it ARE "runs here",
 *  so no edge is needed for that. No handles: edges attach to the workloads. */
function ClusterGroupNode({ data }: NodeProps) {
  const d = data as { label: string; sub?: string };
  return (
    <div className="gnode-cluster">
      <div className="gnode-cluster-head">
        <span className="gnode-cluster-name" title={d.label}>{d.label}</span>
        {d.sub && <span className="gnode-cluster-region">{d.sub}</span>}
      </div>
    </div>
  );
}
/** Workload node — name on one line (ellipsis; full name on hover), namespace as
 *  a distinct sub-line, so a long name can't overflow the fixed-height box. */
function WorkloadNode({ data }: NodeProps) {
  const d = data as { name: string; ns?: string };
  return (
    <div className="gnode-wl" title={d.ns ? `${d.name} · ${d.ns}` : d.name}>
      <Handle type="target" position={Position.Left} isConnectable={false} />
      <div className="gnode-wl-name">{d.name}</div>
      {d.ns && <div className="gnode-wl-ns">{d.ns}</div>}
    </div>
  );
}
const nodeTypes = { clusterGroup: ClusterGroupNode, workload: WorkloadNode };

// Product lines shown first, in this order; anything else sorts after, alphabetically.
const PRODUCT_ORDER = ['scio-core', 'ads-portal', 'optisound', 'optireach', 'optigrowth', 'optiedge'];

// Edge confidence → colour. Monorepo is deterministic (solid), NOT suspect —
// only a genuinely fuzzy link is amber-dashed.
const EDGE_STYLE: Record<string, { stroke: string; dash?: string }> = {
  confirmed: { stroke: '#35c26b' }, // exact image == repo path (single-app repo)
  monorepo: { stroke: '#4f8cff' }, // deterministic sub-path image of a multi-image repo
  inferred: { stroke: '#e0a83a', dash: '6 4' }, // reserved for a genuinely fuzzy match
};

/**
 * GRAPH layer (L1) — the "which app runs where" explorer. Pick a subject in the
 * left rail (search-filtered, so it scales past a dropdown); the canvas shows that
 * subject's 1-hop spine. Clusters are containers: a workload sitting inside a
 * cluster box IS its placement (no line to trace). Everything re-centers on click.
 * Edges (service→workload) carry the stitch confidence: green = confirmed (exact
 * image==repo), blue = monorepo (deterministic sub-path image), amber-dashed =
 * inferred (a genuinely fuzzy link — none today).
 */
export function ServiceGraph() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [filter, setFilter] = useState('');
  const [focus, setFocus] = useState<Focus | null>(null);

  const subjects = useQuery<SubjectsData>(SUBJECTS, { fetchPolicy: 'cache-and-network' });

  // Deep-link: a Global Search hit lands here as /graph?service=… (or ?cluster=…).
  useEffect(() => {
    const svc = sp.get('service');
    const cl = sp.get('cluster');
    if (svc) setFocus({ kind: 'service', name: svc });
    else if (cl) setFocus({ kind: 'cluster', name: cl });
  }, [sp]);

  // Otherwise default to the first service once the list loads.
  useEffect(() => {
    if (focus || !subjects.data || sp.get('service') || sp.get('cluster')) return;
    const first = [...subjects.data.services].sort((a, b) => a.name.localeCompare(b.name))[0];
    if (first) setFocus({ kind: 'service', name: first.name });
  }, [subjects.data, focus, sp]);

  const { data, loading, error } = useQuery<GraphData>(SERVICE_GRAPH, {
    variables: {
      service: focus?.kind === 'service' ? focus.name : null,
      cluster: focus?.kind === 'cluster' ? focus.name : null,
    },
    skip: !focus,
    fetchPolicy: 'cache-and-network',
  });

  // ── left rail groups (search-filtered) ──────────────────────────────────────
  const rail = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const match = (s: string) => !q || s.toLowerCase().includes(q);
    const svcs = (subjects.data?.services ?? []).filter((s) => match(s.name));
    const byLine = new Map<string, string[]>();
    for (const s of svcs) {
      const k = s.productLine || 'other';
      byLine.set(k, [...(byLine.get(k) ?? []), s.name]);
    }
    const rank = (k: string) => {
      const i = PRODUCT_ORDER.indexOf(k);
      return i === -1 ? PRODUCT_ORDER.length : i;
    };
    const serviceGroups = [...byLine.entries()]
      .sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0]))
      .map(([cls, names]) => ({ cls, names: names.sort((x, y) => x.localeCompare(y)) }));
    const clusters = (subjects.data?.clusters ?? [])
      .filter((c) => match(c.name))
      .map((c) => c.name)
      .sort((x, y) => x.localeCompare(y));
    return { serviceGroups, clusters };
  }, [subjects.data, filter]);

  // ── canvas nodes + edges (clusters as containers) ───────────────────────────
  const { nodes, edges } = useMemo(() => {
    const g = data?.serviceGraph;
    if (!g) return { nodes: [] as Node[], edges: [] as Edge[] };

    const byId = new Map(g.nodes.map((n) => [n.id, n]));
    // workload → cluster (from the runsOn edges)
    const wlCluster = new Map<string, string>();
    for (const e of g.edges) {
      if (e.verb === 'runsOn') wlCluster.set(e.source, e.target);
    }
    // cluster → workloads (preserve first-seen order)
    const clWorkloads = new Map<string, string[]>();
    for (const n of g.nodes) {
      if (n.kind !== 'workload') continue;
      const cl = wlCluster.get(n.id);
      if (!cl) continue;
      clWorkloads.set(cl, [...(clWorkloads.get(cl) ?? []), n.id]);
    }
    const services = g.nodes.filter((n) => n.kind === 'service');
    const clusters = g.nodes.filter((n) => n.kind === 'cluster');

    const nodes: Node[] = [];

    // Left column: services.
    services.forEach((s, i) => {
      nodes.push({
        id: s.id,
        data: { label: s.sub ? `${s.label}\n${s.sub}` : s.label },
        position: { x: COL_SVC_X, y: i * 72 },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        className: 'gnode gnode-service',
        style: { width: SVC_W },
        connectable: false,
      });
    });

    // Right column: cluster containers with workload children.
    let y = 0;
    for (const c of clusters) {
      const wls = clWorkloads.get(c.id) ?? [];
      const h = HEAD_H + Math.max(wls.length, 1) * (WL_H + WL_GAP) + PAD;
      nodes.push({
        id: c.id,
        type: 'clusterGroup',
        data: { label: c.label, sub: c.sub },
        position: { x: COL_CL_X, y },
        style: { width: CL_W, height: h },
        selectable: true,
        connectable: false,
      });
      wls.forEach((wlId, i) => {
        const w = byId.get(wlId)!;
        nodes.push({
          id: wlId,
          parentId: c.id,
          extent: 'parent',
          type: 'workload',
          data: { name: w.label, ns: w.sub },
          position: { x: PAD, y: HEAD_H + i * (WL_H + WL_GAP) },
          style: { width: WL_W, height: WL_H },
          connectable: false,
          selectable: false,
        });
      });
      y += h + 24;
    }

    // Edges: service → workload only (containment expresses runsOn). Confidence
    // tiers: confirmed (exact, solid green), monorepo (deterministic sub-path
    // image, solid blue), inferred (genuinely fuzzy, amber dashed + animated).
    const edges: Edge[] = g.edges
      .filter((e) => e.verb === 'deployedTo')
      .map((e) => {
        const s = EDGE_STYLE[e.confidence] ?? EDGE_STYLE.inferred;
        return {
          id: e.id,
          source: e.source,
          target: e.target,
          animated: e.confidence === 'inferred',
          style: { stroke: s.stroke, strokeDasharray: s.dash },
        };
      });

    return { nodes, edges };
  }, [data]);

  const onNodeClick = (_: unknown, node: Node) => {
    const kind = node.id.split(':')[0];
    const label = String((node.data as { label?: string }).label ?? '').split('\n')[0];
    if (kind === 'cl') setFocus({ kind: 'cluster', name: label });
    else if (kind === 'svc') setFocus({ kind: 'service', name: label });
  };

  return (
    <div className="graph-page">
      <div className="graph-body">
        <aside className="graph-rail">
          <input
            className="graph-rail-search"
            placeholder="Filter services & clusters…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <div className="graph-rail-scroll">
            {rail.serviceGroups.map((grp) => (
              <div key={grp.cls} className="graph-rail-section">
                <div className="graph-rail-head">{grp.cls}</div>
                {grp.names.map((name) => (
                  <button
                    key={name}
                    className={`graph-rail-item${focus?.kind === 'service' && focus.name === name ? ' active' : ''}`}
                    onClick={() => setFocus({ kind: 'service', name })}
                  >
                    {name}
                  </button>
                ))}
              </div>
            ))}
            <div className="graph-rail-section">
              <div className="graph-rail-head">clusters</div>
              {rail.clusters.map((name) => (
                <button
                  key={name}
                  className={`graph-rail-item${focus?.kind === 'cluster' && focus.name === name ? ' active' : ''}`}
                  onClick={() => setFocus({ kind: 'cluster', name })}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>
        </aside>

        <section className="graph-canvas">
          <div className="graph-toolbar">
            <span className="graph-focus">
              <h1 className="graph-title">Service graph</h1>
              {focus && (
                <>
                  <span className="badge">{focus.kind}</span>
                  <span className="graph-focus-name">{focus.name}</span>
                </>
              )}
            </span>
            <span className="graph-legend">
              <span className="lg green">— confirmed</span>
              <span className="lg blue">— monorepo</span>
              <span className="lg amber">╌ inferred</span>
              {focus?.kind === 'cluster' && (
                <button className="btn btn-sm" onClick={() => navigate(`/clusters/${encodeURIComponent(focus.name)}`)}>
                  Open cluster detail
                </button>
              )}
            </span>
          </div>

          <div className="graph-flow">
            {error ? (
              <div className="graph-state muted">Failed to load: {error.message}</div>
            ) : loading && !data ? (
              <div className="graph-state muted">Loading…</div>
            ) : nodes.length === 0 ? (
              <div className="graph-state">
                <div className="empty-state">
                  <div className="empty-title">Nothing deployed for this subject</div>
                  <div className="empty-body">
                    Classify repos into services on the Repos page, then run{' '}
                    <code>scan stitch</code> to derive the service → workload edges.
                  </div>
                </div>
              </div>
            ) : (
              <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                onNodeClick={onNodeClick}
                fitView
                minZoom={0.1}
                proOptions={{ hideAttribution: true }}
              >
                <Background />
                <Controls showInteractive={false} />
              </ReactFlow>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
